import React, { useEffect, useRef, useState } from "react";
import { Modal } from "semantic-ui-react";
import { ProductForm } from "./ProductFormPage";
import { ReviewSelectionPage } from "./ReviewSelectionPage";
import { AddSupplyUnitPage } from "./AddSupplyUnitPage";
import type {
  ProductData,
  WizardSupplyUnit,
} from "./AddSupplyUnitWizard/types";

export interface CreateProductWizardProps {
  onClose: (skipConfirmation?: boolean) => void;
  preselectedSupplyUnits?: WizardSupplyUnit[];
  initialProduct?: ProductData | null;
  initialPage?: number;
  mode?: "create" | "duplicate" | "edit";
}

export const CreateProductWizard = ({
  onClose,
  preselectedSupplyUnits = [],
  initialProduct,
  initialPage = 1,
  mode,
}: CreateProductWizardProps) => {
  const [page, setPage] = useState(initialPage);
  const [product, setProduct] = useState<ProductData>({
    title: "",
    partNumber: "",
    families: [],
    createdOn: "",
    createdBy: "",
    productContact: "",
  });
  const [supplyUnits, setSupplyUnits] = useState<WizardSupplyUnit[]>(
    preselectedSupplyUnits
  );
  const contactPicked = useRef(false);

  useEffect(() => {
    if (initialProduct) {
      setProduct({
        product_id: mode === "edit" ? initialProduct.product_id : undefined,
        title: initialProduct.title,
        partNumber: initialProduct.partNumber,
        families: initialProduct.families,
        createdOn: initialProduct.createdOn ?? "",
        createdBy: initialProduct.createdBy ?? "",
        productContact: initialProduct.productContact ?? "",
      });
    }
  }, [initialProduct, mode]);

  const modalTitle =
    mode === "edit"
      ? "Edit Product"
      : mode === "duplicate"
      ? "Create Related Product"
      : "Create New Product";

  const nextPage = () => setPage((curr) => curr + 1);
  const prevPage = () => setPage((curr) => curr - 1);

  const renderPageContent = () => {
    switch (page) {
      case 1:
        return (
          <ProductForm
            preselectedSupplyUnits={preselectedSupplyUnits}
            product={product}
            setProduct={setProduct}
            nextPage={nextPage}
            onClose={onClose}
            contactPicked={contactPicked}
          />
        );
      case 2:
        return (
          <AddSupplyUnitPage
            mode={mode}
            initialProduct={initialProduct}
            preselectedSupplyUnits={preselectedSupplyUnits}
            product={product}
            setProduct={setProduct}
            nextPage={nextPage}
            prevPage={prevPage}
            setSupplyUnits={setSupplyUnits}
            supplyUnits={supplyUnits}
          />
        );
      case 3:
        return (
          <ReviewSelectionPage
            prevPage={prevPage}
            product={product}
            initialProduct={initialProduct}
            setProduct={setProduct}
            supplyUnits={supplyUnits}
            onClose={onClose}
            mode={mode}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose()}
      closeOnDimmerClick={true}
      size="large"
    >
      <Modal.Header>{modalTitle}</Modal.Header>
      {renderPageContent()}
    </Modal>
  );
};
